import React, { useState, useEffect } from 'react';
import { createCenter, updateCenter, getAllUsers } from '../../../services/supabaseService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CENTER_TYPE_OPTIONS = [
  { value: 'coworking', label: 'Coworking' },
  { value: 'managed_office', label: 'Managed Office' },
  { value: 'furnished_office', label: 'Furnished Office' },
  { value: 'bareshell', label: 'Bareshell' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'onboarding', label: 'Onboarding' },
];

const CenterFormModal = ({ center, onClose, onSuccess, userRole }) => {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    location: '',
    address: '',
    city: '',
    state: '',
    country: 'India',
    totalSqft: '',
    totalSeats: '',
    occupiedSeats: '',
    monthlyOperatingCost: '',
    managerId: '',
    contactEmail: '',
    contactPhone: '',
    amenities: [],
    status: 'active',
    centerType: 'coworking',
    inventoryType: 'coworking',
    notes: '',
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [amenityInput, setAmenityInput] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    fetchUsers();
    if (center) {
      setFormData({
        id: center?.id || '',
        name: center?.name || '',
        location: center?.location || '',
        address: center?.address || '',
        city: center?.city || '',
        state: center?.state || '',
        country: center?.country || 'India',
        totalSqft: center?.totalSqft || '',
        totalSeats: center?.totalSeats || '',
        occupiedSeats: center?.occupiedSeats || '',
        monthlyOperatingCost: center?.monthlyOperatingCost || '',
        managerId: center?.managerId || '',
        contactEmail: center?.contactEmail || '',
        contactPhone: center?.contactPhone || '',
        amenities: center?.amenities || [],
        status: center?.status || 'active',
        centerType: center?.centerType || center?.inventoryType || 'coworking',
        inventoryType: center?.centerType || center?.inventoryType || 'coworking',
        notes: center?.notes || '',
      });
    }
  }, [center]);

  const fetchUsers = async () => {
    try {
      const data = await getAllUsers();
      setUsers(data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear validation error when user changes field
    if (validationErrors?.[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleAddAmenity = () => {
    if (amenityInput?.trim() && !formData?.amenities?.includes(amenityInput?.trim())) {
      setFormData((prev) => ({
        ...prev,
        amenities: [...prev?.amenities, amenityInput?.trim()],
      }));
      setAmenityInput('');
    }
  };

  const handleRemoveAmenity = (amenity) => {
    setFormData((prev) => ({
      ...prev,
      amenities: prev?.amenities?.filter((a) => a !== amenity),
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData?.centerType) {
      errors.centerType = 'Center Type is required';
    }
    if (!formData?.status) {
      errors.status = 'Status is required';
    }
    if (!formData?.name?.trim()) {
      errors.name = 'Center Name is required';
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors)?.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Sync inventoryType with centerType for backward compatibility
      const submitData = {
        ...formData,
        centerType: formData?.centerType,
        inventoryType: formData?.centerType,
        status: formData?.status || 'active',
      };

      if (center) {
        await updateCenter(center?.id, submitData);
      } else {
        await createCenter(submitData);
      }
      onSuccess?.();
    } catch (err) {
      console.error('Error saving center:', err);
      setError(err?.message || 'Failed to save center');
    } finally {
      setLoading(false);
    }
  };

  const isSuperAdmin = userRole === 'super_admin';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">{center ? 'Edit Center' : 'Add New Center'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <Icon name="AlertCircle" size={20} className="text-red-500" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Center ID *</label>
                <Input
                  type="text"
                  name="id"
                  value={formData?.id}
                  onChange={handleChange}
                  placeholder="CTR001"
                  required
                  disabled={!!center}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Center Name *</label>
                <Input
                  type="text"
                  name="name"
                  value={formData?.name}
                  onChange={handleChange}
                  placeholder="Mumbai Central Hub"
                  required
                />
                {validationErrors?.name && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors?.name}</p>
                )}
              </div>

              {/* Center Type - Required */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Center Type <span className="text-red-500">*</span>
                </label>
                <Select
                  name="centerType"
                  value={formData?.centerType}
                  onChange={handleChange}
                  required
                >
                  <option value="">-- Select Center Type --</option>
                  {CENTER_TYPE_OPTIONS?.map((opt) => (
                    <option key={opt?.value} value={opt?.value}>
                      {opt?.label}
                    </option>
                  ))}
                </Select>
                {validationErrors?.centerType && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors?.centerType}</p>
                )}
              </div>

              {/* Status - Required, default active */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status <span className="text-red-500">*</span>
                  {!isSuperAdmin && (
                    <span className="text-xs text-gray-400 ml-1">(Super Admin only to change)</span>
                  )}
                </label>
                <Select
                  name="status"
                  value={formData?.status}
                  onChange={handleChange}
                  required
                  disabled={!isSuperAdmin && !!center}
                >
                  {STATUS_OPTIONS?.map((opt) => (
                    <option key={opt?.value} value={opt?.value}>
                      {opt?.label}
                    </option>
                  ))}
                </Select>
                {validationErrors?.status && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors?.status}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                <Input
                  type="text"
                  name="location"
                  value={formData?.location}
                  onChange={handleChange}
                  placeholder="Bandra West"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                <Input
                  type="text"
                  name="city"
                  value={formData?.city}
                  onChange={handleChange}
                  placeholder="Mumbai"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                <Input
                  type="text"
                  name="state"
                  value={formData?.state}
                  onChange={handleChange}
                  placeholder="Maharashtra"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <Input
                type="text"
                name="address"
                value={formData?.address}
                onChange={handleChange}
                placeholder="123 Linking Road, Bandra West"
              />
            </div>
          </div>

          {/* Metrics */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Metrics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Sqft *</label>
                <Input
                  type="number"
                  name="totalSqft"
                  value={formData?.totalSqft}
                  onChange={handleChange}
                  placeholder="15000"
                  required
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Seats *</label>
                <Input
                  type="number"
                  name="totalSeats"
                  value={formData?.totalSeats}
                  onChange={handleChange}
                  placeholder="200"
                  required
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Occupied Seats</label>
                <Input
                  type="number"
                  name="occupiedSeats"
                  value={formData?.occupiedSeats}
                  onChange={handleChange}
                  placeholder="180"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Operating Cost</label>
                <Input
                  type="number"
                  name="monthlyOperatingCost"
                  value={formData?.monthlyOperatingCost}
                  onChange={handleChange}
                  placeholder="500000"
                  min="0"
                />
              </div>
            </div>
          </div>

          {/* Contact & Management */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Contact & Management</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Manager</label>
                <Select name="managerId" value={formData?.managerId} onChange={handleChange}>
                  <option value="">Select Manager</option>
                  {users?.map((user) => (
                    <option key={user?.id} value={user?.id}>
                      {user?.fullName} ({user?.role})
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                <Input
                  type="email"
                  name="contactEmail"
                  value={formData?.contactEmail}
                  onChange={handleChange}
                  placeholder="center@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
                <Input
                  type="tel"
                  name="contactPhone"
                  value={formData?.contactPhone}
                  onChange={handleChange}
                  placeholder="+91-9876543210"
                />
              </div>
            </div>
          </div>

          {/* Amenities */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Amenities</h3>
            <div className="flex gap-2">
              <Input
                type="text"
                value={amenityInput}
                onChange={(e) => setAmenityInput(e?.target?.value)}
                placeholder="Add amenity (e.g., WiFi, Parking)"
                onKeyPress={(e) => e?.key === 'Enter' && (e?.preventDefault(), handleAddAmenity())}
              />
              <Button type="button" onClick={handleAddAmenity}>
                Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData?.amenities?.map((amenity) => (
                <span
                  key={amenity}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                >
                  {amenity}
                  <button type="button" onClick={() => handleRemoveAmenity(amenity)} className="hover:text-blue-900">
                    <Icon name="X" size={14} />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              name="notes"
              value={formData?.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Additional notes about this center..."
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <Button type="button" onClick={onClose} className="bg-gray-200 text-gray-700 hover:bg-gray-300">
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : center ? 'Update Center' : 'Create Center'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CenterFormModal;